import { Color, FrontSide, MeshPhysicalMaterial, Vector2, type Texture } from 'three';
import type { FrameLook } from '../config/frames';

/** Linear RGB transmittance, not a painted tint. Beer–Lambert's optical path
 * grows with incidence angle; Schlick's Fresnel approximation conserves the
 * transmitted/reflected energy. These are visual presets, not filter certification. */
export function lensTransmittance(look: FrameLook): [number, number, number] {
  if (look.lensTransmission) return look.lensTransmission.map(v => Math.max(0.001, Math.min(1, v))) as [number, number, number];
  const tint = new Color(look.lensColor);
  const max = Math.max(tint.r, tint.g, tint.b, 0.001);
  const t = Math.pow(1 - Math.min(0.98, Math.max(0, look.lensOpacity)), 1.6);
  return [tint.r, tint.g, tint.b].map(v => t * (0.82 + 0.18 * v / max)) as [number, number, number];
}
export function opticalTransmission(t: number, cosine: number, ior = 1.5): number {
  const c = Math.max(0, Math.min(1, cosine));
  const cosRefracted = Math.sqrt(1 - (1 - c * c) / (ior * ior));
  const f0 = ((ior - 1) / (ior + 1)) ** 2;
  const fresnel = f0 + (1 - f0) * (1 - c) ** 5;
  return Math.pow(Math.max(0.001, Math.min(1, t)), 1 / cosRefracted) * (1 - fresnel);
}

/** Samples the scene immediately behind the lenses, including the far temple.
 * The framebuffer copy avoids texture feedback and keeps the depth buffer, so
 * the already rendered nose still occludes the lenses correctly. */
export class ScreenLensMaterial extends MeshPhysicalMaterial {
  readonly optics = {
    mataScene: { value: null as Texture | null },
    mataViewport: { value: new Vector2(1, 1) },
    mataTransmission: { value: new Color(1, 1, 1) },
    mataTransmissionTop: { value: new Color(1, 1, 1) },
    mataTransmissionBottom: { value: new Color(1, 1, 1) },
    mataBicolor: { value: 0 },
    mataExposure: { value: 1 },
    mataFocalPx: { value: 1000 },
    mataThickness: { value: 1.4 },
    mataGradient: { value: 0 },
    mataLensCentreY: { value: 0 },
    mataLensHeight: { value: 32.4 },
    mataComposite: { value: 0 },
    mataF0: { value: 0.04 },
  };
  constructor(look: FrameLook) {
    super({ color: look.lensColor, transparent: true, opacity: 1, depthWrite: false, roughness: 0.04,
      metalness: 0, clearcoat: 0, ior: 1.5, specularIntensity: 1, envMapIntensity: 0.65,
      side: FrontSide });
    this.userData.baseOpacity = 1;
    this.userData.baseTransparent = true;
    const [r, g, b] = lensTransmittance(look);
    this.optics.mataTransmission.value.setRGB(r, g, b);
    this.setBicolor(look);
    this.optics.mataGradient.value = Math.max(0, Math.min(0.8, look.lensGradient ?? 0));
    this.optics.mataF0.value = 0.04 + Math.max(0, Math.min(1, look.lensMirror)) * 0.36;
    this.onBeforeCompile = shader => {
      Object.assign(shader.uniforms, this.optics);
      shader.vertexShader = 'varying float mataLocalY;\n' + shader.vertexShader;
      shader.vertexShader = shader.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\nmataLocalY = position.y;');
      shader.fragmentShader = `
        uniform sampler2D mataScene;
        uniform vec2 mataViewport;
        uniform vec3 mataTransmission;
        uniform vec3 mataTransmissionTop, mataTransmissionBottom;
        uniform float mataBicolor, mataExposure, mataFocalPx, mataThickness, mataGradient, mataLensHeight, mataLensCentreY, mataComposite, mataF0;
        varying float mataLocalY;
      ` + shader.fragmentShader;
      shader.fragmentShader = shader.fragmentShader.replace('#include <opaque_fragment>', `
        if (mataComposite > 0.5) {
          float c = clamp(abs(dot(normal, geometryViewDir)), 0.001, 1.0);
          float cosInside = sqrt(1.0 - (1.0 - c*c) / (1.5*1.5));
          float fresnel = mataF0 + (1.0 - mataF0) * pow(1.0 - c, 5.0);
          float height = clamp((mataLocalY - mataLensCentreY) / max(mataLensHeight, 1.0) + 0.5, 0.0, 1.0);
          float path = (1.0 + mataGradient * (height - 0.5)) / cosInside;
          vec3 tint = mix(mataTransmission, mix(mataTransmissionBottom, mataTransmissionTop, smoothstep(0.08, 0.92, height)), mataBicolor);
          vec3 transmitted = pow(tint, vec3(path));
          // First-order displacement through a thin parallel slab (millimetres).
          vec2 shift = normal.xy * mataThickness * (1.0 - 1.0/1.5) * mataFocalPx / max(vViewPosition.z, 50.0);
          vec2 uv = clamp((gl_FragCoord.xy + shift) / mataViewport, vec2(0.001), vec2(0.999));
          vec4 behindTexel = texture2D(mataScene, uv);
          vec3 behind = sRGBTransferEOTF(behindTexel).rgb;
          vec3 reflection = totalSpecular * (mataF0 / 0.04);
          // The copied scene is already exposed. Undo only its exposure before
          // Three's final tone-mapping; highlights still follow scene lighting.
          outgoingLight = behind * transmitted * (1.0-fresnel) / max(mataExposure, 0.001) + reflection;
          // A110Y: no camera copied behind this fragment (empty background, capture in a hidden
          // tab, context just restored): the lens fades out instead of turning black.
          diffuseColor.a *= step(0.5, behindTexel.a);
        }
        #include <opaque_fragment>
      `);
    };
  }
  setLook(look: FrameLook): void {
    this.color.set(look.lensColor);
    this.optics.mataTransmission.value.setRGB(...lensTransmittance(look));
    this.setBicolor(look);
    this.optics.mataGradient.value = Math.max(0, Math.min(0.8, look.lensGradient ?? 0));
    this.optics.mataF0.value = 0.04 + Math.max(0, Math.min(1, look.lensMirror)) * 0.36;
  }
  private setBicolor(look: FrameLook): void {
    const top = look.lensTransmissionTop, bottom = look.lensTransmissionBottom;
    this.optics.mataBicolor.value = top && bottom ? 1 : 0;
    if (top && bottom) {
      this.optics.mataTransmissionTop.value.setRGB(...top);
      this.optics.mataTransmissionBottom.value.setRGB(...bottom);
    }
  }
  override customProgramCacheKey(): string { return 'mata-screen-lens-A110Y2-bicolor-side-centre'; }
}
